import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import Header from 'components/ui/Header';
import Footer from 'pages/landing-page/components/Footer';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const SLUG = 'terms-and-conditions';

const TermsAndConditionsPage = () => {
  const { userProfile, user } = useAuth();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const isEditor = userProfile?.role === 'admin' || userProfile?.role === 'superadmin';

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase?.from('legal_documents')?.select('content_markdown')?.eq('slug', SLUG)?.single();
        if (!error && data) {
          setContent(data?.content_markdown || '');
        }
      } catch (err) {
        console.error('Error fetching legal document:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const handleEdit = () => {
    setEditContent(content);
    setEditMode(true);
    setSaveSuccess(false);
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditContent('');
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      const { error } = await supabase?.from('legal_documents')?.update({
          content_markdown: editContent,
          updated_at: new Date()?.toISOString(),
          updated_by: user?.id,
        })?.eq('slug', SLUG);
      if (!error) {
        setContent(editContent);
        setEditMode(false);
        setEditContent('');
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (err) {
      console.error('Error saving legal document:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="w-full" style={{ paddingTop: 'var(--header-height)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

          <div className="text-center mb-10">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Termini e Condizioni di Utilizzo
            </h1>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-8">
            {isEditor && !editMode && (
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={handleEdit}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  ✏️ Modifica
                </button>
                {saveSuccess && (
                  <span className="text-sm text-green-600 font-medium">✓ Salvato con successo</span>
                )}
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-500 text-sm">Caricamento...</span>
              </div>
            ) : editMode ? (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors"
                  >
                    {saving ? 'Salvataggio...' : '💾 Salva'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300 disabled:opacity-60 transition-colors"
                  >
                    Annulla
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Editor Markdown</p>
                    <textarea
                      value={editContent}
                      onChange={(e) => setEditContent(e?.target?.value)}
                      className="w-full font-mono text-sm border border-gray-300 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                      style={{ minHeight: '400px' }}
                    />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Anteprima</p>
                    <div className="prose prose-lg max-w-none text-gray-700 border border-gray-200 rounded-lg p-4 overflow-auto" style={{ minHeight: '400px' }}>
                      <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                        {editContent}
                      </ReactMarkdown>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="prose prose-lg max-w-none text-gray-700">
                <ReactMarkdown rehypePlugins={[rehypeSanitize]}>
                  {content}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsAndConditionsPage;
